import { FEATURE_CATALOG } from '@weavix/models/src/packaging/feature-catalog';
import { addExtraGrantedActions } from '@weavix/models/src/permission/additional-granted-actions';
import { PermissionAction } from '@weavix/models/src/permission/permissions.model';
import { catalogGates, ProductPackageEntitlements } from './resolve-product-packages';

/**
 * Every action some catalog feature gates, plus the actions those imply, so an implied action follows its package.
 * Product packages decide only these; `enabledActions` decides the rest.
 */
export const PACKAGE_GATED_ACTIONS: ReadonlySet<PermissionAction> = withImpliedActions([...catalogGates(FEATURE_CATALOG).actions]);

const expandedActions = new WeakMap<ProductPackageEntitlements, ReadonlySet<PermissionAction>>();

/** The actions `entitlements` license, expanded with the actions they imply; cached per entitlements object. */
export function entitledActions(entitlements: ProductPackageEntitlements): ReadonlySet<PermissionAction> {
    let actions = expandedActions.get(entitlements);
    if (!actions) {
        actions = withImpliedActions([...entitlements.actions]);
        expandedActions.set(entitlements, actions);
    }
    return actions;
}

/**
 * Whether product packages license `action`: undefined when packages do not decide it (not gated, or nothing resolved),
 * so the caller uses `enabledActions`; never an empty answer that would lock users out.
 */
export function isPackageEntitled(action: PermissionAction, entitlements: ProductPackageEntitlements | null | undefined): boolean | undefined {
    if (!entitlements || !PACKAGE_GATED_ACTIONS.has(action)) return undefined;
    return entitledActions(entitlements).has(action);
}

/** `actions` plus every action they imply. */
function withImpliedActions(actions: PermissionAction[]): ReadonlySet<PermissionAction> {
    addExtraGrantedActions(actions);
    return new Set(actions);
}
